import React, { useState, useCallback } from 'react'

import { Stack, Spinner, useToast } from '@pnld/components-web'

import {
  useDashboardPredictionYears,
  useDashboardPrediction,
} from '@/api/dashboard/queries'
import { DashboardPredictionPayload } from '@/api/dashboard/types'
import { PredictionYear, CensusYear } from '@/types/dashboard'
import { getErrorMessage } from '@/utils/api'

import { GeoMapFilter, GeoMapFilterValues } from '../parts'
import { PredictionSingleYear, PredictionMultiYear } from './parts'

const Prediction: React.FC = () => {
  const toast = useToast()
  const [filterValues, setFilterValues] = useState<GeoMapFilterValues | null>(
    null
  )

  const errorHandler = useCallback(
    (error: unknown) => {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
    },
    [toast]
  )

  const mapGeoMapFilterToPredictionPayload = useCallback(
    (values: GeoMapFilterValues): DashboardPredictionPayload => {
      const nuPredictionYear: PredictionYear[] = []
      const nuCensusYear: CensusYear[] = []

      const years = (
        Array.isArray(values.predictionYears)
          ? values.predictionYears
          : values.predictionYears
          ? [values.predictionYears]
          : []
      ) as string[]
      years.forEach((yearStr: string) => {
        const [predictionYear, baseCensusYear] = yearStr.split('#')
        if (predictionYear && baseCensusYear) {
          nuPredictionYear.push(Number(predictionYear))
          nuCensusYear.push(Number(baseCensusYear))
        }
      })

      return {
        nuPredictionYear,
        nuCensusYear,
        coRegion: values.region || undefined,
        coUf: values.states.length ? values.states : undefined,
        coCity: values.cities.length ? values.cities : undefined,
        coEntity: values.schools.length ? values.schools : undefined,
        tpDependency: values.dependencies.length
          ? values.dependencies
          : undefined,
        tpLocation: values.locations.length ? values.locations : undefined,
        coSphere: values.spheres.length ? values.spheres : undefined,
        stPnldService: values.hasPndlService || undefined,
        coEducationStage: values.educationStages.length
          ? values.educationStages
          : undefined,
        coYearGrade: values.yearGrades.length ? values.yearGrades : undefined,
      }
    },
    []
  )

  const { data: predictionYears, isLoading: isPredictionYearsLoading } =
    useDashboardPredictionYears({ onError: errorHandler })

  const { data: prediction, isLoading: isPredictionLoadingRaw } =
    useDashboardPrediction(
      filterValues
        ? mapGeoMapFilterToPredictionPayload(filterValues)
        : ({} as DashboardPredictionPayload),
      { onError: errorHandler, enabled: !!filterValues }
    )

  const isPredictionLoading = isPredictionLoadingRaw && !!filterValues

  const predictionsYearOptions =
    (predictionYears || []).map(year => ({
      label: year.label,
      value: `${year.predictionYear}#${year.censusYear}`,
    })) || []

  const isMultiYear = filterValues
    ? Array.isArray(filterValues.predictionYears) &&
      filterValues.predictionYears.length > 1
    : false

  return (
    <Stack gap={4}>
      <GeoMapFilter
        predictionYears={{
          value: predictionsYearOptions,
          isLoading: isPredictionYearsLoading,
          isMulti: true,
        }}
        onChangeFilter={values => {
          if (
            values.predictionYears &&
            ((Array.isArray(values.predictionYears) &&
              values.predictionYears.length > 0) ||
              (typeof values.predictionYears === 'string' &&
                values.predictionYears.trim() !== ''))
          ) {
            setFilterValues(values as GeoMapFilterValues)
          } else {
            setFilterValues(null)
          }
        }}
      />

      {isPredictionLoading && (
        <Stack alignItems="center" justifyContent="center">
          <Spinner size="xl" />
        </Stack>
      )}

      {!isPredictionLoading && prediction && !isMultiYear && (
        <PredictionSingleYear
          prediction={prediction[0]}
          globalFilters={filterValues}
        />
      )}

      {!isPredictionLoading && prediction && isMultiYear && (
        <PredictionMultiYear
          prediction={prediction}
          globalFilters={filterValues}
        />
      )}
    </Stack>
  )
}

export default Prediction
